<template>
  <HeadTitle
    title="Asignador de supervisores"
    description="Vista de asignación de supervisores para empleados de una nómina"
  />
  <br />
  <div class="card flex justify-content-start align-items-center pt-3">
    <label class="pr-3 text-700 font-semibold">Seleccionar nómina</label>
    <Dropdown
      v-model="selectedPayroll"
      filter
      :options="formattedPayrollss"
      optionLabel="formattedLabel"
      placeholder="0000-Nómina"
      @change="refreshData"
      class="w-full md:w-22rem"
    >
      <template #value="slotProps">
        <div v-if="slotProps.value" class="flex align-items-center">
          <div>{{ slotProps.value.code }}-{{ slotProps.value.name }}</div>
        </div>
        <span v-else>
          {{ slotProps.placeholder }}
        </span>
      </template>
      <template #option="slotProps">
        <div class="flex align-items-center">
          <div>{{ slotProps.option.code }} - {{ slotProps.option.name }}</div>
        </div>
      </template>
    </Dropdown>
    <div class="flex flex-row ml-6">
      <FileUpload
        mode="basic"
        accept=".csv, .xls, .xlsx"
        chooseLabel="Carga masiva"
        class="text-sm font-light"
        severity="secondary"
        custom-upload
        @uploader="removeFile"
        @select="saveFile($event)"
        style="background: #e5e7eb; border: #e4e4e4; color: #495057"
      />
      <InfoButton
        message="Solo se permite subir un archivo de tipo csv o xlsx con las columnas CORREO_SUPERVISOR y CODIGO_EMPLEADO."
        class="ml-2"
      />
    </div>
  </div>
  <ConfirmDialog></ConfirmDialog>
  <div class="card p-fluid pt-3">
    <DataTable
      v-model:editingRows="editingRows"
      :value="employees.data"
      editMode="row"
      dataKey="id"
      paginator
      :rows="pagination.limit"
      :rowsPerPageOptions="[5, 10, 20, 50]"
      @row-edit-save="onRowEditSave"
      tableClass="editable-cells-table"
      tableStyle="min-width: 50rem"
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      :currentPageReportTemplate="currentPageReportTemplate"
      :totalRecords="employees.totalItems"
      @page="onPageChange"
      scrollHeight="600px"
      scrollable
      class="tabla"
    >
      <template #header>
        <div class="flex justify-content-end">
          <span class="p-input-icon-left w-4">
            <i class="pi pi-search" />
            <InputText
              v-model="search"
              placeholder="Buscar"
              @change="searcher"
            />
          </span>
        </div>
      </template>
      <Column
        field="supervisor"
        header="Supervisor"
        style="width: 40%"
        filterField="supervisor"
        :showFilterMenu="false"
        :show-filter-match-modes="false"
        :filterMenuStyle="{ width: '14rem' }"
      >
        <template #editor="{ data, field, index }">
          <Dropdown
            v-model="data[field]"
            :options="[
              { id: null, name: 'Sin asignar (remover)' },
              ...(approvers || []),
            ]"
            optionLabel="name"
            :placeholder="
              data[field] ? data[field].name : 'Selecciona un aprobador'
            "
            filter
            @change="
              (e) => {
                if (e.value?.id === null) data[field] = null
              }
            "
          >
            <template #option="slotProps">
              <span v-if="slotProps.option.id === null" class="text-pink-600">
                {{ slotProps.option.name }}
              </span>
              <span v-else>
                {{ slotProps.option.name }}
              </span>
            </template>
          </Dropdown>
        </template>
        <template #body="{ data }">
          <span v-if="data.supervisor">{{ data.supervisor.name }}</span>
          <span class="font-medium text-pink-600" v-else>Sin asignar</span>
        </template>
      </Column>
      <Column header="Empleado" style="width: 40%">
        <template #body="{ data }">
          {{ data.code }} - {{ data.name }}
        </template>
      </Column>
      <template #empty>No se encontraron empleados.</template>
      <template #loading> Cargando informacion de los empleados...</template>
      <Column :rowEditor="true" style="width: 10%"></Column>
      <Column>
        <template #body="{ data }">
          <AuditButton :record-ids="[data.id]" :entities="['Employee']" />
        </template>
      </Column>
    </DataTable>
  </div>
  <Footer>
    <Button
      class="mr-2 text-sm font-light"
      label="Guardar cambios"
      icon="pi pi-save"
      @click="save()"
      severity="primary"
    />
  </Footer>
</template>
<script setup lang="ts">
  import Footer from '@/shared/components/Footer.vue'
  import EmployeeSupervisor from '@/modules/employees/models/EmployeeSupervisort'
  import { geEmployeesByPayrollid } from '@/modules/employees/services/EmployeeService'
  import { getPayrollsByCompanyId } from '@/modules/payrolls-config/services/PayrollService'
  import AssignApprover from '@/modules/approvers/models/AssignApprover'
  import useCompany from '@/store/company'
  import { storeToRefs } from 'pinia'
  import { computed, onMounted, reactive, ref } from 'vue'
  import {
    assignApprovers,
    getApproversByCompanyId,
    validateBulkAssignation,
  } from '../services/ApproverService'
  import useNotifications from '@/store/notification'
  import useLoading from '@/store/load'
  import AuditButton from '@/shared/components/AuditButton.vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import InfoButton from '@/shared/components/InfoButton.vue'
  import { useConfirm } from 'primevue/useconfirm'

  const selectedPayroll = ref()
  const payrolls = ref()
  const approvers = ref()
  const loadingStore = useLoading()
  const companyStore = useCompany()
  const { company } = storeToRefs(companyStore)
  const editingRows = ref([])
  const approversToAssign = ref<AssignApprover[]>([])
  const notificationStore = useNotifications()
  const confirm = useConfirm()
  const search = ref('')
  const importFile = ref()
  const pagination = reactive({
    currentPage: 0,
    lastPage: 0,
    limit: 10,
  })

  const employees = reactive({
    data: [] as EmployeeSupervisor[],
    totalItems: 0,
  })

  companyStore.$subscribe(async (_, state) => {
    loadingStore.setLoadingState()
    company.value = state.company
    if (!company.value) {
      loadingStore.dismissLoadingState()
      return
    }
    const payrollResponse = await getPayrollsByCompanyId(
      company.value?.id || ''
    )
    payrolls.value = payrollResponse
    const approversResponse = await getApproversByCompanyId(
      company.value?.id || ''
    )
    approvers.value = approversResponse
    loadingStore.dismissLoadingState()
  })

  onMounted(async () => {
    if (company.value) {
      loadingStore.setLoadingState()
      const payrollResponse = await getPayrollsByCompanyId(
        company.value?.id || ''
      )
      payrolls.value = payrollResponse
      const approversResponse = await getApproversByCompanyId(
        company.value?.id || ''
      )
      approvers.value = approversResponse
      loadingStore.dismissLoadingState()
    }
  })

  const refreshData = async () => {
    loadingStore.setLoadingState()
    const response = await geEmployeesByPayrollid({
      payrollId: selectedPayroll.value?.id || '',
    })

    if (!response) {
      loadingStore.dismissLoadingState()
      return
    }

    const { data, totalItems } = response
    employees.data = data
    employees.totalItems = totalItems
    loadingStore.dismissLoadingState()
  }

  const onPageChange = async (event: any) => {
    const { first, rows } = event
    pagination.limit = rows
    pagination.currentPage = Math.floor(first / rows + 1)
    pagination.lastPage = Math.floor(employees.totalItems / pagination.limit)
  }

  const onRowEditSave = (event: any) => {
    let { data, newData, index } = event

    employees.data[index] = newData

    approversToAssign.value.push({
      employeeId: newData.id,
      approverId: newData.supervisor?.id ?? null,
    })
  }

  const save = async () => {
    const response = await assignApprovers(approversToAssign.value)
    if (response === 'OK') {
      notificationStore.addSuccesNotification({
        detail: `201`,
        severity: 'success',
        summary: `Se han asignado los supervisores correctamente`,
      })
      approversToAssign.value = []
    }
  }

  const searcher = async () => {
    //use an timeout before made the request
    setTimeout(async () => {
      loadingStore.setLoadingState()
      const { data, totalItems } = await geEmployeesByPayrollid({
        payrollId: selectedPayroll.value?.id || '',
        pagination: {
          search: search.value,
        },
      })
      employees.data = data
      employees.totalItems = totalItems
      loadingStore.dismissLoadingState()
    }, 200)
  }

  const formattedPayrollss = computed(() => {
    return payrolls.value?.map((payroll: any) => ({
      ...payroll,
      formattedLabel: `${payroll?.code}-${payroll?.name}`,
    }))
  })

  const saveFile = async (event: any) => {
    if (!selectedPayroll.value?.id || !company.value?.id) {
      notificationStore.addErrorNotification({
        detail: 'Debes seleccionar una nómina y una empresa',
        severity: 'error',
        summary: 'Error al subir archivo',
      })

      // Cancel file selection
      event.preventDefault()
      return false
    }
    // Additional validations (optional)
    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ]
    if (!allowedTypes.includes(event.files[0].type)) {
      notificationStore.addErrorNotification({
        detail: 'Tipo de archivo no permitido',
        severity: 'error',
        summary: 'Solo se aceptan CSV y Excel',
      })
      event.preventDefault()
      return false
    }

    confirm.require({
      message:
        '¿Estás seguro de que deseas asignar los supervisores masivamente?',
      header: 'Confirmación',
      icon: 'pi pi-exclamation-circle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      acceptLabel: 'Si',
      rejectLabel: 'No',
      accept: async () => {
        loadingStore.setLoadingState()
        importFile.value = event.files[0]
        const response = await validateBulkAssignation({
          companyId: company.value?.id || '',
          payrollId: selectedPayroll.value?.id || '',
          file: event.files[0],
        })
        loadingStore.dismissLoadingState()
        if (response === 'OK') {
          notificationStore.addSuccesNotification({
            detail: `201`,
            severity: 'success',
            summary: `Archivos procesados con exito.`,
          })
          await refreshData()
        }
      },
      reject: () => {},
    })
  }

  const removeFile = () => {
    importFile.value = null
  }

  const currentPageReportTemplate = computed(() => {
    return `{first} a {last} de ${employees.totalItems}`
  })
</script>
<style>
  .tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 90%;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  :deep(.p-paginator) {
    display: flex;
    align-items: center;
    justify-content: end;
    flex-wrap: wrap;
    font-size: 14px;
  }
</style>
